import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Search, Users, CalendarDays, Package, Handshake, X } from 'lucide-react';
import { STAGE_LABELS, label } from '@/lib/labels';

export function GlobalSearch() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const ref = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!query.trim() || query.trim().length < 2) {
      setResults(null);
      setOpen(false);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.get('/search', { params: { q: query.trim(), limit: 5 } });
        setResults(res.data);
        setOpen(true);
      } catch { setResults(null); }
      setLoading(false);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const go = (path) => {
    setOpen(false);
    setQuery('');
    setResults(null);
    navigate(path);
  };

  const sections = [
    { key: 'contacts', label: 'Contatti', icon: Users, items: results?.contacts || [],
      render: (c) => ({ primary: c.full_name || c.email, secondary: `${label(STAGE_LABELS, c.stage)} ${c.company ? '— ' + c.company : ''}`, path: `/contacts?open=${c.id}` }) },
    { key: 'events', label: 'Eventi', icon: CalendarDays, items: results?.events || [],
      render: (e) => ({ primary: e.name, secondary: `${e.event_type} — ${e.event_date ? new Date(e.event_date).toLocaleDateString('it-CH', { day: '2-digit', month: 'short' }) : ''}`, path: `/events?open=${e.id}` }) },
    { key: 'products', label: 'Prodotti', icon: Package, items: results?.products || [],
      render: (p) => ({ primary: p.name, secondary: p.product_type || p.status || '', path: `/products?open=${p.id}` }) },
    { key: 'deals', label: 'Deal', icon: Handshake, items: results?.deals || [],
      render: (d) => ({ primary: d.title || d.contact_name || d.contact_email, secondary: d.stage || '', path: `/sales?open=${d.id}` }) },
  ];

  return (
    <div className="relative" ref={ref} data-testid="global-search">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results?.total > 0 && setOpen(true)}
          placeholder="Cerca contatti, eventi, prodotti, deal..."
          className="w-full pl-9 pr-8 py-2 text-sm border border-[#E2E8F0] rounded-lg bg-[#F8FAFC] focus:outline-none focus:ring-2 focus:ring-[#2E75B6]/30 focus:border-[#2E75B6] transition-colors"
          data-testid="global-search-input"
        />
        {query && (
          <button onClick={() => { setQuery(''); setResults(null); setOpen(false); }} className="absolute right-2.5 top-1/2 -translate-y-1/2">
            <X className="w-3.5 h-3.5 text-[#94A3B8] hover:text-[#64748B]" />
          </button>
        )}
      </div>

      {open && results && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#E2E8F0] rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto" data-testid="search-results">
          {results.total === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-[#94A3B8]">Nessun risultato per "{query}"</div>
          ) : (
            sections.filter(s => s.items.length > 0).map(section => {
              const Icon = section.icon;
              return (
                <div key={section.key}>
                  <div className="px-3 py-1.5 bg-[#F8FAFC] border-b border-[#E2E8F0] flex items-center gap-2">
                    <Icon className="w-3.5 h-3.5 text-[#64748B]" />
                    <span className="text-[10px] font-semibold text-[#64748B] uppercase tracking-wider">{section.label} ({section.items.length})</span>
                  </div>
                  {section.items.map(item => {
                    const { primary, secondary, path } = section.render(item);
                    return (
                      <div key={item.id} onClick={() => go(path)}
                        className="px-3 py-2 hover:bg-[#F1F5F9] cursor-pointer border-b border-[#F1F5F9] last:border-0"
                        data-testid={`search-result-${item.id}`}>
                        <p className="text-sm font-medium text-[#1A3A5C]">{primary}</p>
                        {secondary && <p className="text-xs text-[#64748B]">{secondary}</p>}
                      </div>
                    );
                  })}
                </div>
              );
            })
          )}
          {loading && (
            <div className="px-4 py-2 text-center text-xs text-[#94A3B8]">Ricerca in corso...</div>
          )}
        </div>
      )}
    </div>
  );
}
